// Barra de operaciones del inspector — el elemento firma del portal.
// Consola de sala de control: wordmark, identidad de la sesión, reloj UTC
// vivo, countdown de expiración del access token, switch RUN/HOLD del
// polling y selector de idioma. Todo funcional: cada indicador refleja
// estado real.

import { useEffect, useState, type ReactNode } from 'react';
import { Activity, LogOut, Pause, Play } from 'lucide-react';
import { logout } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { useSession } from '@/hooks/useSession';
import { LangSwitch } from './LangSwitch';
import { StatusLed } from './StatusLed';

function useNowSeconds(): number {
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));
  useEffect(() => {
    const id = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => clearInterval(id);
  }, []);
  return now;
}

function fmtCountdown(seconds: number): string {
  if (seconds <= 0) return '00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
    : `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

export function InspectorShell({
  polling,
  onTogglePolling,
  children,
}: {
  polling: boolean;
  onTogglePolling: () => void;
  children: ReactNode;
}) {
  const session = useSession();
  const { t } = useI18n();
  const now = useNowSeconds();
  const remaining = session ? session.expires_at - now : 0;
  const utc = new Date(now * 1000).toISOString().slice(11, 19);

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b border-surface-border-strong bg-surface-muted/95 backdrop-blur">
        <div className="mx-auto flex max-w-[1600px] flex-wrap items-center gap-x-5 gap-y-1 px-4 py-2 text-[11px]">
          <span className="flex items-center gap-2 text-sm font-bold tracking-[0.2em] text-brand">
            <Activity className="h-4 w-4" aria-hidden="true" />
            AUTHSYS//INSPECTOR
          </span>
          <span className="rounded border border-status-warning/50 px-1.5 py-0.5 text-[10px] uppercase tracking-widest text-status-warning">
            {t('shell.envBadge')}
          </span>

          <span className="ml-auto flex flex-wrap items-center gap-x-5 gap-y-1">
            {session && (
              <>
                <span className="text-ink-muted">
                  <span className="text-ink-subtle">{t('shell.user')} </span>
                  {session.email}
                </span>
                <span className="text-ink-muted">
                  <span className="text-ink-subtle">{t('shell.partner')} </span>
                  {session.partner_id}
                </span>
                <span
                  className={`tabular-nums ${remaining < 300 ? 'text-status-warning' : 'text-ink-muted'}`}
                  title={t('shell.tokenTitle')}
                >
                  <span className="text-ink-subtle">{t('shell.token')} </span>
                  {fmtCountdown(remaining)}
                </span>
              </>
            )}
            <span className="tabular-nums text-ink-muted" title={t('shell.utcTitle')}>
              <span className="text-ink-subtle">UTC </span>
              {utc}
            </span>
            <button
              type="button"
              onClick={onTogglePolling}
              aria-pressed={polling}
              className="flex min-h-[2rem] items-center gap-1.5 rounded border border-surface-border-strong px-2 py-0.5 uppercase tracking-wider text-ink hover:border-brand focus-visible:ring-2 focus-visible:ring-brand"
              title={polling ? t('shell.pauseTitle') : t('shell.resumeTitle')}
            >
              {polling ? (
                <>
                  <StatusLed tone="success" pulse /> {t('shell.run')}
                  <Pause className="h-3 w-3" aria-hidden="true" />
                </>
              ) : (
                <>
                  <StatusLed tone="warning" /> {t('shell.hold')}
                  <Play className="h-3 w-3" aria-hidden="true" />
                </>
              )}
            </button>
            <LangSwitch />
            {session && (
              <button
                type="button"
                onClick={logout}
                className="flex min-h-[2rem] items-center gap-1.5 rounded border border-surface-border px-2 py-0.5 uppercase tracking-wider text-ink-muted hover:border-status-error hover:text-status-error focus-visible:ring-2 focus-visible:ring-brand"
              >
                <LogOut className="h-3 w-3" aria-hidden="true" /> {t('shell.logout')}
              </button>
            )}
          </span>
        </div>
      </header>
      <main id="main" className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-4">
        {children}
      </main>
      <footer className="border-t border-surface-border px-4 py-2 text-center text-[10px] text-ink-subtle">
        {t('shell.footer')}
      </footer>
    </div>
  );
}
